import { Link } from "wouter";
import { ArrowRight, Award, Users, Clock, Target } from "lucide-react";
import { Reveal } from "./reveal";

const SECTORS = [
  { img: `${import.meta.env.BASE_URL}images/home-residential.webp`, alt: "VertexAir engineer working on Fujitsu outdoor air conditioning units", tag: "For homeowners", title: "Residential", desc: "Whisper-quiet systems designed to blend seamlessly into your home's aesthetic while providing absolute comfort.", cta: "Home Solutions" },
  { img: `${import.meta.env.BASE_URL}images/home-commercial-installation.webp`, alt: "Ceiling cassette air conditioning and ductwork installation in commercial premises", tag: "For businesses & trade", title: "Commercial", desc: "Robust, scalable VRF and VRV systems engineered for offices, retail spaces, and server rooms requiring critical cooling.", cta: "Business Solutions" },
];

const TRUST = [
  { Icon: Award, title: "Certified Engineers", desc: "REFCOM, F-Gas, NICEIC and Gas Safe registered for complete peace of mind." },
  { Icon: Users, title: "Experienced Team", desc: "15+ years of hands-on experience across residential and commercial projects." },
  { Icon: Clock, title: "24/7 Support", desc: "Emergency callouts available around the clock, 365 days a year." },
  { Icon: Target, title: "Precision Work", desc: "Meticulous attention to detail on every installation, repair and service." },
];

export function Sectors() {
  return (
    <section className="py-14 lg:py-20 bg-[hsl(var(--vx-paper))]">
      <div className="container mx-auto max-w-7xl px-4">
        <Reveal className="mb-12">
          <p className="vx-eyebrow text-[hsl(var(--vx-blue))] mb-4">04 — Who we work with</p>
          <h2 className="vx-display text-4xl md:text-5xl font-bold text-[hsl(var(--vx-ink))] max-w-2xl">One team for homes and businesses.</h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-5">
          {SECTORS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.1}>
              <Link href="/installation" className="group relative flex min-h-[460px] rounded-[1.75rem] overflow-hidden" data-testid={`link-sector-${s.title.toLowerCase()}`}>
                <img src={s.img} alt={s.alt} width={1200} height={800} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--vx-ink)/0.95)] via-[hsl(var(--vx-ink)/0.3)] to-transparent" />
                <div className="relative mt-auto p-8 md:p-10 text-white">
                  <span className="inline-block rounded-full bg-white/15 backdrop-blur px-3 py-1 text-xs font-semibold mb-4">{s.tag}</span>
                  <h3 className="vx-display text-4xl font-bold mb-3">{s.title}</h3>
                  <p className="text-white/80 text-lg mb-7 max-w-md">{s.desc}</p>
                  <span className="inline-flex items-center gap-2 rounded-full bg-white text-[hsl(var(--vx-ink))] px-5 py-2.5 font-semibold transition-colors group-hover:bg-[hsl(var(--vx-ember))] group-hover:text-white">
                    {s.cta} <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 rounded-[1.75rem] bg-white ring-1 ring-[hsl(var(--vx-mist))] overflow-hidden">
          {TRUST.map(({ Icon, title, desc }, i) => (
            <Reveal key={title} delay={i * 0.06} className="p-8 border-[hsl(var(--vx-mist))] sm:[&:nth-child(odd)]:border-r lg:border-r lg:last:border-r-0 border-b lg:border-b-0">
              <Icon className="w-7 h-7 text-[hsl(var(--vx-blue))] mb-6" aria-hidden="true" />
              <h3 className="font-display text-xl font-bold text-[hsl(var(--vx-ink))] mb-2">{title}</h3>
              <p className="text-slate-600 text-[0.95rem] leading-relaxed">{desc}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
